// R-based position sizing calculator. Given account value, entry, stop,
// and max-risk-% per trade, computes how many shares to buy and the
// resulting notional / R-distance. Edgewonk + Van Tharp convention.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calculator } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtMoney, fmtNumber } from '@/lib/format';
import { cn } from '@/lib/cn';

type Props = {
  // Optional anchors for the detail page; if provided the inputs prefill.
  entry?: number;
  stop?: number;
  currencySymbol?: string;
};

export function PositionSizer({ entry = 100, stop = 95, currencySymbol = '£' }: Props) {
  const summary = useQuery({ queryKey: ['summary'], queryFn: api.accountSummary });
  const [maxRiskPct, setMaxRiskPct] = useState(1.0);
  const [entryPrice, setEntryPrice] = useState(entry);
  const [stopPrice, setStopPrice] = useState(stop);

  const account = summary.data?.totalValue ?? 0;

  const calc = useMemo(() => {
    const riskBudget = (account * maxRiskPct) / 100;
    const r = Math.abs(entryPrice - stopPrice);
    if (r === 0 || riskBudget <= 0) {
      return { shares: 0, notional: 0, riskBudget, r, accountPct: 0 };
    }
    const shares = Math.floor(riskBudget / r);
    const notional = shares * entryPrice;
    const accountPct = account > 0 ? (notional / account) * 100 : 0;
    return { shares, notional, riskBudget, r, accountPct };
  }, [account, maxRiskPct, entryPrice, stopPrice]);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-3">
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="size-4 text-indigo-300" />
        <h3 className="text-sm font-medium text-zinc-200">Position sizing</h3>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2">
        <Field label="Entry">
          <input
            type="number"
            step="0.01"
            value={entryPrice}
            onChange={(e) => setEntryPrice(Number(e.target.value))}
            className="bg-transparent outline-none w-full text-sm num"
          />
        </Field>
        <Field label="Stop">
          <input
            type="number"
            step="0.01"
            value={stopPrice}
            onChange={(e) => setStopPrice(Number(e.target.value))}
            className="bg-transparent outline-none w-full text-sm num"
          />
        </Field>
        <Field label="Risk %">
          <input
            type="number"
            step="0.1"
            min={0.05}
            max={10}
            value={maxRiskPct}
            onChange={(e) => setMaxRiskPct(Number(e.target.value))}
            className="bg-transparent outline-none w-full text-sm num"
          />
        </Field>
        <Field label="Account">
          <span className="text-sm num text-zinc-300">{fmtMoney(account)}</span>
        </Field>
      </div>

      <div className="rounded-md border border-indigo-500/30 bg-indigo-500/[0.06] p-3 space-y-1.5">
        <Row label="Buy" value={`${fmtNumber(calc.shares, 0)} shares`} emphasised />
        <Row label="Notional" value={`${currencySymbol}${fmtNumber(calc.notional, 0)}`} />
        <Row label="Risk budget (1R)" value={fmtMoney(calc.riskBudget)} />
        <Row
          label="% of account"
          value={`${calc.accountPct.toFixed(1)}%`}
          tone={calc.accountPct > 10 ? 'warn' : undefined}
        />
      </div>

      <p className="text-[10px] text-zinc-400 mt-2 leading-snug">
        Buy size = (account × max risk %) ÷ |entry − stop|. Default 1% per trade
        is the Van Tharp standard for discretionary discipline.
      </p>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-0.5 px-2 py-1.5 rounded border border-zinc-800 bg-zinc-900/40">
      <span className="text-[10px] uppercase tracking-wider text-zinc-400">{label}</span>
      {children}
    </label>
  );
}

function Row({
  label,
  value,
  emphasised,
  tone,
}: {
  label: string;
  value: string;
  emphasised?: boolean;
  tone?: 'warn';
}) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-zinc-400">{label}</span>
      <span
        className={cn(
          'num',
          emphasised ? 'text-base font-medium text-zinc-50' : 'text-zinc-200',
          tone === 'warn' && 'text-amber-400',
        )}
      >
        {value}
      </span>
    </div>
  );
}
